import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import ScrudixLogo from './ScrudixLogo';
import { captureBoundaryError } from '../services/errorTracker';
import { isDynamicImportChunkError, clearChunkReloadFlag, purgeServiceWorkerCaches, forceHardReload } from '../services/lazyWithRetry';

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Scrudix] Unhandled error:', error, errorInfo);
    captureBoundaryError(error, errorInfo.componentStack ?? undefined);
  }

  handleReset = () => {
    if (isDynamicImportChunkError(this.state.error)) {
      this.handleHardReload();
      return;
    }
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  handleHardReload = () => {
    clearChunkReloadFlag();
    if (typeof window !== 'undefined') {
      purgeServiceWorkerCaches().finally(() => {
        forceHardReload();
      });
    }
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = isDynamicImportChunkError(this.state.error);

      return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-zinc-50 via-white to-zinc-100 dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 px-6">
          <div className="max-w-md w-full text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center">
              <ScrudixLogo size="md" />
            </div>
            <h1 className="text-2xl font-black tracking-tight text-zinc-900 dark:text-white mb-3">
              {isChunkError ? 'New version available' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
              {isChunkError
                ? 'A new version of Scrudix has been deployed or your network session was refreshed. Click below to reload and sync the latest assets.'
                : 'Scrudix encountered an unexpected issue. Your data is safe — click below to reload.'}
            </p>
            {this.state.error && (
              <div className="mb-6 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-4 text-left">
                <p className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-2">Error Details</p>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 font-mono break-all leading-relaxed">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-bold text-sm hover:bg-black dark:hover:bg-zinc-100 transition-all hover:scale-105 active:scale-95"
              >
                Try Again
              </button>
              <button
                onClick={this.handleHardReload}
                className="px-6 py-3 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl font-bold text-sm text-zinc-600 dark:text-zinc-400 hover:border-zinc-400 transition-all hover:scale-105 active:scale-95"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
